import { API_CONFIG } from '@/config/api';

// HERE API Types
export interface HereLocation {
  id: string;
  title: string;
  address: {
    label: string;
    countryCode: string;
    countryName: string;
    stateCode?: string;
    state?: string;
    county?: string;
    city?: string;
    district?: string;
    street?: string;
    houseNumber?: string;
    postalCode?: string;
  };
  position: {
    lat: number;
    lng: number;
  };
  resultType: string;
  houseNumberType?: string;
  localityType?: string;
}

export interface SavedAddress {
  id: string;
  label: string;
  address: string;
  coordinates: {
    lat: number;
    lng: number;
  };
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface LocationSuggestion {
  id: string;
  title: string;
  address: string;
  coordinates: {
    lat: number;
    lng: number;
  };
  type: 'saved' | 'suggestion' | 'current';
  structuredAddress?: {
    houseNumber: string;
    street: string;
    city: string;
    county: string;
    postalCode: string;
    country: string;
  };
}

class HereLocationService {
  private readonly apiKey: string;
  private readonly baseUrl = 'https://geocode.search.hereapi.com/v1';
  private readonly autocompleteUrl = 'https://autocomplete.search.hereapi.com/v1';
  
  // Cache for session
  private cache = new Map<string, any>();
  private readonly cacheTimeout = 5 * 60 * 1000; // 5 minutes

  constructor() {
    this.apiKey = API_CONFIG.HERE_API_KEY;
    
    // Validate API key on initialization
    if (!this.apiKey || this.apiKey === 'YOUR_API_KEY_HERE') {
      console.warn('⚠️ HERE API key not configured properly');
    }
  }

  /**
   * Get address suggestions as user types
   */
  async getAddressSuggestions(query: string, options?: {
    at?: { lat: number; lng: number };
    countryCode?: string;
    limit?: number;
  }): Promise<LocationSuggestion[]> {
    // Don't trim the query - preserve spaces for better search
    const searchQuery = query;
    if (searchQuery.length < 2) return []; // Reduced from 3 to 2 for better UX

    const cacheKey = `autocomplete_${searchQuery}_${JSON.stringify(options)}`;
    const cached = this.getFromCache(cacheKey);
    if (cached) return cached;

    try {
      console.log('🔍 Starting HERE API request for query:', `"${searchQuery}"`, 'Length:', searchQuery.length);
      
      // Use URLSearchParams to properly encode the query including spaces
      const params = new URLSearchParams({
        q: searchQuery, // Use original query with spaces
        apiKey: this.apiKey, // Changed from 'apikey' to 'apiKey'
        limit: (options?.limit || 8).toString(),
        lang: 'en',
      });

      console.log('📋 Request parameters:', {
        query: searchQuery,
        apiKey: this.apiKey ? 'Present' : 'Missing',
        limit: options?.limit || 8,
        lang: 'en'
      });

      // Bias results to Ireland by default
      if (options?.at) {
        params.append('at', `${options.at.lat},${options.at.lng}`);
      } else {
        // Default to Dublin, Ireland
        params.append('at', '53.3498,-6.2603');
      }

      if (options?.countryCode) {
        params.append('in', `countryCode:${options.countryCode}`);
      } else {
        // Default to Ireland
        params.append('in', 'countryCode:IRL');
      }

      console.log('🔍 HERE API autocomplete request:', `${this.autocompleteUrl}/autocomplete?${params.toString()}`);
      
      // Use autocomplete endpoint for real-time suggestions
      const response = await fetch(`${this.autocompleteUrl}/autocomplete?${params.toString()}`);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('HERE API autocomplete error:', response.status, errorText);
        throw new Error(`HERE API error: ${response.status}`);
      }

      const data = await response.json();
      console.log('📥 HERE API autocomplete response:', data);
      
      if (!data.items || data.items.length === 0) {
        console.log('⚠️ No suggestions found for query:', searchQuery);
        // Return a fallback suggestion so user can still proceed
        return [{
          id: 'manual_fallback',
          title: searchQuery,
          address: searchQuery,
          coordinates: { lat: 53.3498, lng: -6.2603 }, // Default to Dublin
          type: 'suggestion',
        }];
      }
      
      const suggestions: LocationSuggestion[] = (data.items || []).map((item: any) => {
        console.log('🏠 HERE API item:', item);
        
        return {
          id: item.id || `suggestion_${Date.now()}_${Math.random()}`,
          title: item.title || item.address?.label || '',
          address: item.address?.label || item.title || '',
          coordinates: {
            lat: item.position?.lat || 0,
            lng: item.position?.lng || 0,
          },
          type: 'suggestion' as const,
          // Add structured address data if available
          structuredAddress: item.address ? {
            houseNumber: item.address.houseNumber || '',
            street: item.address.street || '',
            city: item.address.city || item.address.district || '',
            county: item.address.county || item.address.state || '',
            postalCode: item.address.postalCode || '',
            country: item.address.countryName || item.address.countryCode || '',
          } : undefined,
        };
      });

      this.setCache(cacheKey, suggestions);
      return suggestions;
    } catch (error) {
      console.error('HERE API autocomplete error:', error);
      return [];
    }
  }

  /**
   * Geocode a full address to get precise coordinates
   */
  async geocodeAddress(address: string): Promise<HereLocation | null> {
    const cacheKey = `geocode_${address}`;
    const cached = this.getFromCache(cacheKey);
    if (cached) return cached;

    try {
      const params = new URLSearchParams({
        q: address,
        apiKey: this.apiKey,
        limit: '1',
        lang: 'en',
      });

      const response = await fetch(`${this.baseUrl}/geocode?${params}`);
      
      if (!response.ok) {
        throw new Error(`HERE API error: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.items || data.items.length === 0) {
        return null;
      }

      const location = data.items[0] as HereLocation;
      this.setCache(cacheKey, location);
      return location;
    } catch (error) {
      console.error('HERE API geocoding error:', error);
      return null;
    }
  }

  /**
   * Reverse geocode coordinates to get address
   */
  async reverseGeocode(lat: number, lng: number): Promise<HereLocation | null> {
    const cacheKey = `reverse_${lat}_${lng}`;
    const cached = this.getFromCache(cacheKey);
    if (cached) return cached;

    try {
      // HERE API v7 reverse geocoding endpoint - use correct parameter name
      const params = new URLSearchParams({
        at: `${lat},${lng}`,
        apikey: this.apiKey, // Use 'apikey' not 'apiKey' for reverse geocoding
        limit: '1',
        lang: 'en',
      });

      console.log('🔍 HERE API reverse geocoding request:', `https://revgeocode.search.hereapi.com/v1/revgeocode?${params.toString()}`);

      // Use the correct HERE API v7 endpoint
      const response = await fetch(`https://revgeocode.search.hereapi.com/v1/revgeocode?${params}`);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('HERE API reverse geocoding error response:', response.status, errorText);
        
        // If it's a 400 error, try with different parameter format
        if (response.status === 400) {
          console.log('🔄 Retrying with different parameter format...');
          
          // Try with different endpoint format
          const altParams = new URLSearchParams({
            at: `${lat},${lng}`,
            apiKey: this.apiKey, // Try with apiKey
            limit: '1',
            lang: 'en',
          });
          
          const altResponse = await fetch(`https://revgeocode.search.hereapi.com/v1/revgeocode?${altParams}`);
          
          if (!altResponse.ok) {
            console.error('HERE API alternative format also failed:', altResponse.status);
            return null;
          }
          
          const altData = await altResponse.json();
          if (!altData.items || altData.items.length === 0) {
            return null;
          }
          
          const location = altData.items[0] as HereLocation;
          this.setCache(cacheKey, location);
          return location;
        }
        
        throw new Error(`HERE API error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('📥 HERE API reverse geocoding response:', data);
      
      if (!data.items || data.items.length === 0) {
        return null;
      }

      const location = data.items[0] as HereLocation;
      this.setCache(cacheKey, location);
      return location;
    } catch (error) {
      console.error('HERE API reverse geocoding error:', error);
      return null;
    }
  }

  /**
   * Get current location using browser geolocation
   */
  async getCurrentLocation(): Promise<{ lat: number; lng: number } | null> {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          console.error('Geolocation error:', error);
          resolve(null);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000, // 5 minutes
        }
      );
    });
  }

  /**
   * Get current location with address
   */
  async getCurrentLocationWithAddress(): Promise<LocationSuggestion | null> {
    try {
      const coords = await this.getCurrentLocation();
      if (!coords) return null;

      const location = await this.reverseGeocode(coords.lat, coords.lng);
      if (!location) return null;

      return {
        id: 'current_location',
        title: 'Current Location',
        address: location.address.label,
        coordinates: coords,
        type: 'current',
        // Add structured address data from reverse geocoding
        structuredAddress: {
          houseNumber: location.address.houseNumber || '',
          street: location.address.street || '',
          city: location.address.city || location.address.district || '',
          county: location.address.county || location.address.state || '',
          postalCode: location.address.postalCode || '',
          country: location.address.countryName || location.address.countryCode || '',
        },
      };
    } catch (error) {
      console.error('Error getting current location with address:', error);
      return null;
    }
  }

  /**
   * Cache management
   */
  private getFromCache(key: string): any {
    const cached = this.cache.get(key);
    if (!cached) return null;

    const { data, timestamp } = cached;
    if (Date.now() - timestamp > this.cacheTimeout) {
      this.cache.delete(key);
      return null;
    }

    return data;
  }

  private setCache(key: string, data: any): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
    });
  }

  /**
   * Clear cache
   */
  clearCache(): void {
    this.cache.clear();
  }
}

// Singleton instance
export const hereLocationService = new HereLocationService();

// Saved addresses management (localStorage for now, can be moved to backend later)
export class SavedAddressService {
  private readonly storageKey = 'rapidfix_saved_addresses';

  /**
   * Get all saved addresses for current user
   */
  getSavedAddresses(): SavedAddress[] {
    try {
      const saved = localStorage.getItem(this.storageKey);
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('Error loading saved addresses:', error);
      return [];
    }
  }

  /**
   * Save a new address
   */
  saveAddress(address: Omit<SavedAddress, 'id' | 'createdAt' | 'updatedAt'>): SavedAddress {
    const addresses = this.getSavedAddresses();
    
    // Check for duplicates
    const existing = addresses.find(addr => 
      Math.abs(addr.coordinates.lat - address.coordinates.lat) < 0.001 &&
      Math.abs(addr.coordinates.lng - address.coordinates.lng) < 0.001
    );

    if (existing) {
      return existing;
    }

    const newAddress: SavedAddress = {
      ...address,
      id: `addr_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    // If this is the first address or marked as default, make it default
    if (addresses.length === 0 || address.isDefault) {
      // Remove default from others
      addresses.forEach(addr => addr.isDefault = false);
      newAddress.isDefault = true;
    }

    addresses.push(newAddress);
    this.saveToStorage(addresses);
    return newAddress;
  }

  /**
   * Update an existing address
   */
  updateAddress(id: string, updates: Partial<SavedAddress>): SavedAddress | null {
    const addresses = this.getSavedAddresses();
    const index = addresses.findIndex(addr => addr.id === id);
    
    if (index === -1) return null;

    addresses[index] = {
      ...addresses[index],
      ...updates,
      updatedAt: new Date().toISOString(),
    };

    // Handle default address logic
    if (updates.isDefault) {
      addresses.forEach((addr, i) => {
        if (i !== index) addr.isDefault = false;
      });
    }

    this.saveToStorage(addresses);
    return addresses[index];
  }

  /**
   * Delete an address
   */
  deleteAddress(id: string): boolean {
    const addresses = this.getSavedAddresses();
    const index = addresses.findIndex(addr => addr.id === id);
    
    if (index === -1) return false;

    const wasDefault = addresses[index].isDefault;
    addresses.splice(index, 1);

    // If we deleted the default address, make the first one default
    if (wasDefault && addresses.length > 0) {
      addresses[0].isDefault = true;
    }

    this.saveToStorage(addresses);
    return true;
  }

  /**
   * Get default address
   */
  getDefaultAddress(): SavedAddress | null {
    const addresses = this.getSavedAddresses();
    return addresses.find(addr => addr.isDefault) || addresses[0] || null;
  }

  /**
   * Set default address
   */
  setDefaultAddress(id: string): boolean {
    const addresses = this.getSavedAddresses();
    const targetIndex = addresses.findIndex(addr => addr.id === id);
    
    if (targetIndex === -1) return false;

    addresses.forEach((addr, index) => {
      addr.isDefault = index === targetIndex;
    });

    this.saveToStorage(addresses);
    return true;
  }

  private saveToStorage(addresses: SavedAddress[]): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(addresses));
    } catch (error) {
      console.error('Error saving addresses:', error);
    }
  }
}

// Singleton instance
export const savedAddressService = new SavedAddressService();